import React, { useCallback, useEffect, useState } from "react";
import axios, { AxiosError } from "axios";
import { toast } from "react-toastify";
import { FaBuilding, FaCog } from "react-icons/fa";
import { crudDe } from "../../../../services/acesso";
import api from "../../../../services/api";
import Listagem from "../../../../components/listagem";
import ModalForm from "../../../../components/modal-form";
import { AcaoGrid, ApiError, Campo } from "../../../../services/tipos";
import { TipoFormulario, ESTADOS_BRASIL } from "../../../../services/constantes";
import { obterIdTenent, obterIdUsuario, usuarioTemPermissao } from "../../../../services/seguranca";
import { useAppSelector } from "../../../../store/hooks";
import { LocaSmartLayout } from "../../../../components/locasmart-layout";
import ConfiguracaoFilialTab from "../../../../components/configuracao-filial";

interface Filial {
    id?: string;
    nome: string;
    cnpj?: string;
    telefone?: string;
    cep?: string;
    logradouro?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    complemento?: string;
    ativa: boolean;
    idEmpresa: string;
    comissaoTipoCalculo?: number;
    comissaoValor?: number | null;
}

function FilialListagem() {
    const token = useAppSelector(state => state.token);
    const [filiais, setFiliais] = useState<Filial[]>([]);
    const [carregando, setCarregando] = useState(false);
    const [realizarConsulta, setRealizarConsulta] = useState(true);
    const [exibirFormModal, setExibirFormModal] = useState(false);
    const [modo, setModo] = useState(TipoFormulario.tpCadastro);
    const [filialSelecionada, setFilialSelecionada] = useState<Filial | null>(null);
    const [integracaoAberta, setIntegracaoAberta] = useState<'asaas' | 'whatsapp' | 'autentique' | 'azulmonitor' | 'pix' | null>(null);
    const [filialConfig, setFilialConfig] = useState<Filial | null>(null);
    const [mascaraDocumento, setMascaraDocumento] = useState('999.999.999-99');
    const [entidadeParaAtualizar, setEntidadeParaAtualizar] = useState<any>({});
    const [forcarAtualizacao, setForcarAtualizacao] = useState(false);
    const [comissaoTipo, setComissaoTipo] = useState(0);

    const temPermissao = usuarioTemPermissao(token, 'filial_visualizar');

    useEffect(() => {
        if (exibirFormModal) setComissaoTipo(Number(filialSelecionada?.comissaoTipoCalculo) || 0);
    }, [exibirFormModal, filialSelecionada]);

    const buscarPorCep = async (cep: string) => {
        try {
            cep = cep.replace(/\D/g, '');
            if (cep.length !== 8) return;
            const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
            const infoCep = response.data;
            if (infoCep.erro) return;
            setEntidadeParaAtualizar({
                logradouro: infoCep.logradouro,
                bairro: infoCep.bairro,
                cidade: infoCep.localidade,
                estado: infoCep.uf,
            });
            setForcarAtualizacao(prev => !prev);
        } catch { }
    };

    const estadosOptions = ESTADOS_BRASIL.map(uf => ({ valor: uf.sigla, label: uf.nome }));

    const colunas = [
        { field: 'nome', headerName: 'Nome', flex: 2 },
        { field: 'cnpj', headerName: 'CNPJ', flex: 1 },
        { field: 'endereco', headerName: 'Endereço', flex: 2 },
        { field: 'telefone', headerName: 'Telefone', flex: 1 },
        {
            field: 'ativa', headerName: 'Status', flex: 1,
            renderCell: (params: any) => (
                <span style={{
                    padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                    backgroundColor: params.value ? '#e8f5e9' : '#fbe9e7',
                    color: params.value ? '#2e7d32' : '#c62828'
                }}>
                    {params.value ? 'Ativa' : 'Inativa'}
                </span>
            )
        },
    ];

    const campos: Campo[] = [
        { label: 'Nome da Unidade', nome: 'nome', required: true, tipo: 'text', largura: 6, coluna: 1, linha: 1, visible: true },
        { label: 'Telefone', nome: 'telefone', required: false, tipo: 'text', largura: 6, coluna: 2, linha: 1, visible: true, mascara: '(**) *****-****' },
        {
            label: '', nome: 'tipoDocumento', required: false, tipo: 'toggle', largura: 4, coluna: 1, linha: 2, visible: true,
            defaultValueToggle: 'cpf',
            opcoes: [{ label: 'CPF', valor: 'cpf' }, { label: 'CNPJ', valor: 'cnpj' }],
            onCampoDigitar: (e: any) => {
                setMascaraDocumento(e === 'cnpj' ? '99.999.999/9999-99' : '999.999.999-99');
            }
        },
        { label: 'CPF/CNPJ', nome: 'cnpj', required: false, tipo: 'text', largura: 4, coluna: 1, linha: 3, visible: true, mascara: mascaraDocumento },
        {
            label: 'CEP', nome: 'cep', required: false, tipo: 'text', largura: 4, coluna: 2, linha: 3, visible: true, mascara: '*****-***',
            onCampoSair(e: any) {
                if (e && e.replace(/\D/g, '').length === 8) buscarPorCep(e);
            }
        },
        { label: 'Logradouro', nome: 'logradouro', required: false, tipo: 'text', largura: 8, coluna: 1, linha: 4, visible: true },
        { label: 'Número', nome: 'numero', required: false, tipo: 'text', largura: 4, coluna: 2, linha: 4, visible: true },
        { label: 'Bairro', nome: 'bairro', required: false, tipo: 'text', largura: 4, coluna: 1, linha: 5, visible: true },
        { label: 'Cidade', nome: 'cidade', required: false, tipo: 'text', largura: 4, coluna: 2, linha: 5, visible: true },
        { label: 'Estado', nome: 'estado', required: false, tipo: 'select', largura: 4, coluna: 3, linha: 5, visible: true, opcoes: estadosOptions },
        { label: 'Complemento', nome: 'complemento', required: false, tipo: 'text', largura: 12, coluna: 1, linha: 6, visible: true },
        {
            label: 'Comissão da unidade (sugerida na locação)', nome: 'comissaoTipoCalculo', required: false, tipo: 'select',
            largura: 6, coluna: 1, linha: 7, visible: true,
            opcoes: [{ label: 'Nenhuma', valor: 0 }, { label: 'Percentual (%)', valor: 1 }, { label: 'Fixo (R$)', valor: 2 }],
            onCampoDigitar: (e: any) => setComissaoTipo(Number(e) || 0),
            ajudinha: { texto: 'Comissão padrão sugerida ao criar uma locação desta unidade. Pode ser ajustada em cada locação.' }
        },
        {
            label: 'Valor da comissão', nome: 'comissaoValor', required: false,
            tipo: comissaoTipo === 1 ? 'percentual' : 'money',
            largura: 6, coluna: 2, linha: 7, visible: () => comissaoTipo !== 0
        },
    ];

    function tratarErro(error: AxiosError<ApiError>) {
        setCarregando(false);
        const data = error.response?.data;
        if (data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
            const erros = data.errors as Record<string, string[]>;
            Object.keys(erros).forEach(campo => {
                erros[campo].forEach((msg: string) => toast.error(msg));
            });
            return;
        }
        const msg = data?.Mensagem || error.message || 'Erro desconhecido';
        toast.error('Não foi possível continuar a operação: ' + msg);
    }

    useEffect(() => {
        if (!temPermissao || !realizarConsulta) return;

        const fetchFiliais = async () => {
            setCarregando(true);
            try {
                // Verifica se usuário tem filiais atribuídas — se sim, mostra só as dele
                const respUsuario = await api.get(`/Filial/PorUsuario/${obterIdUsuario(token)}`);
                const filiaisUsuario = respUsuario.data || [];
                const response = filiaisUsuario.length > 0
                    ? respUsuario
                    : await api.get(`/Filial/PorEmpresa/${obterIdTenent(token)}`);
                setFiliais(response.data || []);
                setRealizarConsulta(false);
            } catch (error) {
                tratarErro(error as AxiosError<ApiError>);
            } finally {
                setCarregando(false);
            }
        };

        fetchFiliais();
    }, [temPermissao, realizarConsulta, token]);

    const handleNovo = useCallback(() => {
        if (usuarioTemPermissao(token, 'filial_novo')) {
            setModo(TipoFormulario.tpCadastro);
            setFilialSelecionada(null);
            setExibirFormModal(true);
        } else {
            toast.error('Você não tem permissão para cadastrar uma nova unidade');
        }
    }, [token]);

    const handleEditar = useCallback((filial: Filial) => {
        if (usuarioTemPermissao(token, 'filial_editar')) {
            setModo(TipoFormulario.tpEdicao);
            setFilialSelecionada(filial);
            setExibirFormModal(true);
        } else {
            toast.error('Você não tem permissão para editar uma unidade');
        }
    }, [token]);

    const handleExcluir = useCallback(async (filial: Filial) => {
        if (!usuarioTemPermissao(token, 'filial_excluir')) {
            toast.error('Você não tem permissão para excluir uma unidade');
            return;
        }

        try {
            await api.delete(`/Filial/${filial.id}`);
            toast.success('Unidade excluída com sucesso');
            setRealizarConsulta(true);
        } catch (error) {
            tratarErro(error as AxiosError<ApiError>);
        }
    }, [token]);

    const handleVisualizacao = useCallback((filial: Filial) => {
        setModo(TipoFormulario.tpVisualizacao);
        setFilialSelecionada(filial);
        setExibirFormModal(true);
    }, []);

    const abrirIntegracao = useCallback((filial: Filial, tipo: 'asaas' | 'whatsapp' | 'autentique' | 'azulmonitor' | 'pix') => {
        setFilialConfig(filial);
        setIntegracaoAberta(tipo);
    }, []);

    const acoesExtras: AcaoGrid[] = [
        { Texto: 'Asaas (Pagamentos)', Icone: FaCog, Cor: '#1F467E', OnClick: (e: any) => abrirIntegracao(e, 'asaas') },
        { Texto: 'Chave Pix', Icone: FaCog, Cor: '#32BCAD', OnClick: (e: any) => abrirIntegracao(e, 'pix') },
        { Texto: 'WhatsApp', Icone: FaCog, Cor: '#25D366', OnClick: (e: any) => abrirIntegracao(e, 'whatsapp') },
        { Texto: 'Autentique', Icone: FaCog, Cor: '#6C63FF', OnClick: (e: any) => abrirIntegracao(e, 'autentique') },
        { Texto: 'LocaSmartTrack', Icone: FaCog, Cor: '#0088CC', OnClick: (e: any) => abrirIntegracao(e, 'azulmonitor') },
    ];

    const onSave = useCallback(async (entidade: any) => {
        try {
            const filial: Filial = {
                nome: entidade.nome,
                cnpj: entidade.cnpj,
                telefone: entidade.telefone,
                cep: entidade.cep,
                logradouro: entidade.logradouro,
                numero: entidade.numero,
                bairro: entidade.bairro,
                cidade: entidade.cidade,
                estado: entidade.estado,
                complemento: entidade.complemento,
                ativa: true,
                idEmpresa: obterIdTenent(token),
                comissaoTipoCalculo: Number(entidade.comissaoTipoCalculo) || 0,
                comissaoValor: (Number(entidade.comissaoTipoCalculo) || 0) === 0 ? null : (entidade.comissaoValor || 0),
                ...(modo === TipoFormulario.tpEdicao && { id: entidade.id })
            };

            if (modo === TipoFormulario.tpCadastro) {
                await api.post('/Filial', filial);
                toast.success('Unidade criada com sucesso');
            } else {
                await api.put('/Filial', filial);
                toast.success('Unidade atualizada com sucesso');
            }

            setRealizarConsulta(true);
            setExibirFormModal(false);
        } catch (error) {
            tratarErro(error as AxiosError<ApiError>);
        }
    }, [token, modo]);

    return (
        <LocaSmartLayout>
            <Listagem
                    permissoes={crudDe('filial')}
                carregando={carregando}
                linhas={filiais}
                colunas={colunas}
                nomeEntidade="Unidade"
                nomePropriedade="nome"
                handleCriar={handleNovo}
                handleEditar={handleEditar}
                handleExclusao={handleExcluir}
                handleVisualizacao={handleVisualizacao}
                handleSelecionarEntidade={setFilialSelecionada}
                IconeEntidade={FaBuilding}
                acoesExtras={acoesExtras}
            />

            <ModalForm
                show={exibirFormModal}
                onHide={() => setExibirFormModal(false)}
                onCancel={() => setExibirFormModal(false)}
                titulo={modo === TipoFormulario.tpCadastro ? "Nova Unidade" : modo === TipoFormulario.tpVisualizacao ? "Visualizando Unidade" : "Editando Unidade"}
                campos={campos}
                modo={modo}
                entidadeSelecionada={filialSelecionada}
                onSave={onSave}
                carregando={carregando}
                tamanho="lg"
                urlTutorial="https://youtu.be/Muop5Vzy2QY"
                atualizacaoEntidade={{
                    entidadeUsadaParaAtualizacao: entidadeParaAtualizar,
                    forcarAtualizacao: forcarAtualizacao
                }}
                apiConfig={{
                    endpoint: '/Filial',
                    requisicaoBemSucedida: () => { setRealizarConsulta(true); setExibirFormModal(false); },
                    tratarErro: tratarErro,
                    idTenentCampo: 'idEmpresa'
                }}
            />

            {filialConfig?.id && (
                <ConfiguracaoFilialTab
                    idFilial={filialConfig.id!}
                    show={!!integracaoAberta}
                    onHide={() => setIntegracaoAberta(null)}
                    nomeFilial={filialConfig.nome}
                    integracao={integracaoAberta || 'asaas'}
                />
            )}
        </LocaSmartLayout>
    );
}

export default FilialListagem;
